/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { from, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import {
	FancySupplementalAction,
	SupplementalActionTypes,
} from 'src/app/app-data/actions/supplemental-actions';
import { Plan } from '@touchplan/common';
import { LocationsApi } from 'src/app/app-data/state/LocationsApi';
import { ProjectMembersApi } from 'src/app/app-data/state/ProjectMembersApi';
import { RolesApi } from 'src/app/app-data/state/RolesApi';
import { TicketList, TicketListFactory } from 'src/app/common/fancy-firebase-ticket/services/ticket-factory';
import { RefOrObservable } from 'src/app/common/fancy-firebase/base/interfaces';
import { PriorityList } from 'src/app/common/fancy-firebase/lists/PriorityList';
import { VarianceList } from 'src/app/common/fancy-firebase/lists/VarianceList';
import { PlanObject } from 'src/app/common/fancy-firebase/objects/PlanObject';
import { makeStandardTicketJoiners } from 'src/app/common/fancy-firebase/util/utils';
import { delayedRetry } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { FancyActionTrackerFactory } from 'src/app/common/fancy-firebase/services/fancy-action-tracker-factory.service';
import { FancyFirebaseProjectsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-projects-api-builder.service';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';

import firebase from 'firebase/compat/app';

/**
 * This class/ service is meant to both hide away some of the boilerplate around constructing mobile specific ticket lists. And provide
 * some utility functions surrounding that usecase.
 */

@Injectable({
	providedIn: 'root',
})
export class TicketListMaker {
	private angularLand = {
		angularFireDatabase: this.angularFireDatabase,
		fancyActionTrackerFactory: this.fancyActionTrackerFactory,
	};

	constructor(
		public planAccessCheck: PlanAccessCheck,
		private ticketListFactory: TicketListFactory,
		private fancyFirebaseProjectsApiBuilder: FancyFirebaseProjectsApiBuilder,
		private angularFireDatabase: AngularFireDatabase,
		private fancyActionTrackerFactory: FancyActionTrackerFactory
	) {}

	/**
	 * Creates a ticket list built from a firebase query and only loads a single ticket. Basically used just the cover the use case
	 * of directly loading a ticket edit view when there's no guarentee of that ticket ever existing in the list it's parent component
	 * contains. This is done _mostly_ because a huge amount of logic is currently built into the [[TicketList]]
	 */
	public createSingleTicketList(
		projectId: string,
		planId: string,
		ticketId: string,
		rolesApi: RolesApi,
		locationsApi: LocationsApi,
		projectMembersApi: ProjectMembersApi,
		priorityList: PriorityList,
		varianceList: VarianceList
	) {
		const singleTicket$ = this.makeSingleTicketListQuery(projectId, planId, ticketId);
		const singleTicketList = this.constructTicketList(
			projectId,
			planId,
			rolesApi,
			locationsApi,
			projectMembersApi,
			priorityList,
			varianceList,
			singleTicket$
		);

		return singleTicketList;
	}

	/**
	 * A special variant of creating a single ticket list that gets ticketList from [[createSingleTicketList]] but waits until the ticket
	 * has actually loaded. This gives us a hook to wait on mostly for the router resolver. The returned observable will emit null if the
	 * ticket doesn't actually exist.
	 */
	public waitForAndCreateSingleTicketList(
		projectId: string,
		planId: string,
		ticketId: string,
		rolesApi: RolesApi,
		locationsApi: LocationsApi,
		projectMembersApi: ProjectMembersApi,
		priorityList: PriorityList,
		varianceList: VarianceList
	) {
		const singleTicketList = this.createSingleTicketList(
			projectId,
			planId,
			ticketId,
			rolesApi,
			locationsApi,
			projectMembersApi,
			priorityList,
			varianceList
		);

		// - call checkFirebaseForTicketDirectly
		// - convert to observable
		// - set up some retry and eventually give up mechanism to cover the various other things loading and that stupid hack the angularfire
		// team added
		return from(this.checkFirebaseForTicketDirectly(projectId, planId, ticketId)).pipe(
			switchMap((snapshot) => {
				if (!snapshot.exists()) {
					singleTicketList.destroy();
					return of(null as TicketList);
				}

				return of(singleTicketList).pipe(delayedRetry((list) => list.syncList.size > 0, 100, 50));
			}),
			take(1)
		);
	}

	/**
	 * Make a quick query directly on the firebase to see if the ticket exists
	 */
	private checkFirebaseForTicketDirectly(projectId: string, planId: string, ticketId: string) {
		const path = fancyFirebaseGetters.getTicketPath(projectId, planId, ticketId);
		const ref = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId);
		return ref.get();
	}

	/**
	 * The query of a single ticket that still behaves like a list
	 */
	private makeSingleTicketListQuery(projectId: string, planId: string, ticketId: string) {
		const path = fancyFirebaseGetters.getTicketPath(projectId, planId);
		const ref = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId);

		const list = this.angularFireDatabase.list(ref, (afRef) => {
			return afRef.orderByKey().equalTo(ticketId);
		});

		return list.stateChanges();
	}

	// can replace the version in ticket.service with a call to this one, but... them merge conflicts
	public constructTicketList(
		projectId: string,
		planId: string,
		rolesApi: RolesApi,
		locationsApi: LocationsApi,
		projectMembersApi: ProjectMembersApi,
		priorityList: PriorityList,
		varianceList: VarianceList,
		ticketListDataSource: RefOrObservable
	) {
		const ticketListJoiners = [
			...makeStandardTicketJoiners(rolesApi.data, locationsApi.data, projectMembersApi.data, priorityList),
		];

		const ticketList = this.ticketListFactory.create(ticketListDataSource, ticketListJoiners);
		ticketList.addSupplementalObservable(
			varianceList.list$.pipe(
				<any>map((list) => {
					return new FancySupplementalAction<Plan>(
						list as any,
						SupplementalActionTypes.varianceListUpdated,
						'varianceList'
					);
				})
			)
		);

		const planPath = fancyFirebaseGetters.getPlanPath(projectId, planId);
		const plan = new PlanObject(
			this.angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planPath, projectId)
		);

		const supTicketPlanEvents = plan.object$.pipe(
			map((data) => {
				return new FancySupplementalAction<Plan>(data, SupplementalActionTypes.planUpdated, planId);
			})
		);
		//calcs side, pins, longest ticket in column
		ticketList.addSupplementalObservable(supTicketPlanEvents as any);

		// sketchy thing ported from ProjectList to allow writes on these "derived" lists... still fairly sketchy but... battle tested?
		// will _not_ work after implementing "all" view
		const ticketListPath = fancyFirebaseGetters.getTicketPath(projectId, planId);
		const ticketListRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			ticketListPath,
			projectId
		);
		ticketList.listRef = this.angularFireDatabase.list(ticketListRef);

		ticketList.addDestroyer(() => plan.destroy());

		return ticketList;
	}
}
